/** Action button with CVA variants for visual style and size. Wraps React Aria Button. */
import { cva, type VariantProps } from 'class-variance-authority';
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from 'react-aria-components';
import { Link, type LinkProps } from 'react-router-dom';
import { cn } from '@/foundation/utils/cn';

export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 font-semibold rounded-xl transition-all duration-200 btn-press',
    'cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed',
    'focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--f-accent)]',
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-[var(--f-accent)] text-[var(--f-fg-on-accent)] hover:bg-[var(--f-accent-hover)] disabled:hover:bg-[var(--f-accent)]',
        secondary:
          'bg-[var(--f-bg-sunken)] text-[var(--f-fg)] hover:bg-[var(--f-bg-raised)] active:bg-[var(--f-bg-sunken)]',
        outline:
          'border border-[var(--f-border-strong)] bg-transparent text-[var(--f-fg)] hover:bg-[var(--f-bg-sunken)]',
        ghost: 'bg-transparent text-[var(--f-fg-secondary)] hover:bg-[var(--f-bg-sunken)]',
        danger:
          'bg-[var(--f-error)] text-[var(--f-fg-on-accent)] hover:bg-[var(--f-error-hover)] disabled:hover:bg-[var(--f-error)]',
        success:
          'bg-[var(--f-success)] text-[var(--f-fg-on-accent)] hover:bg-[var(--f-success-hover)] disabled:hover:bg-[var(--f-success)]',
        accent:
          'bg-[var(--f-accent)] text-[var(--f-fg-on-accent)] hover:bg-[var(--f-accent-hover)] disabled:hover:bg-[var(--f-accent)]',
        sky: 'bg-[var(--f-info)] text-[var(--f-fg-on-accent)] hover:bg-[var(--f-info)] disabled:hover:bg-[var(--f-info)]',
        link: 'bg-transparent text-[var(--f-accent-text)] hover:text-[var(--f-accent-hover)] p-0 h-auto font-medium',
        'link-danger':
          'bg-transparent text-[var(--f-error-text)] hover:text-[var(--f-error-hover)] p-0 h-auto font-medium',
      },
      size: {
        sm: 'px-3 py-1 text-sm',
        md: 'px-4 py-2 text-sm',
        lg: 'px-6 py-3 text-base',
        icon: 'p-2',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;

interface ButtonProps extends AriaButtonProps, ButtonVariants {
  className?: string;
}

export function Button({ variant, size, className, ...props }: ButtonProps) {
  return <AriaButton className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}

interface LinkButtonProps extends LinkProps, ButtonVariants {
  className?: string;
}

export function LinkButton({ variant, size, className, ...props }: LinkButtonProps) {
  return <Link className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}
